import type {
  CodingResult,
  CodingResultsSubtopic,
  CodingResultSubTopicTotal,
  SentimentCode,
} from 'src/types/dashboard';
import type { SentimentFilter } from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

export const INSIGHT_WHEEL_TOPICS_MAX = 20;
export const INSIGHT_WHEEL_TOPICS_MIN = 3;

export const getSubtopicsTotal = (topics: CodingResult[]) =>
  topics.flatMap(({ topicsTotal }) => topicsTotal);

export const getMinValue = (
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) =>
  subTopics.length < INSIGHT_WHEEL_TOPICS_MIN
    ? subTopics.length
    : INSIGHT_WHEEL_TOPICS_MIN;

export const getMaxValue = (
  subTopics: CodingResultsSubtopic[] | CodingResultSubTopicTotal[],
) => {
  if (subTopics.length > INSIGHT_WHEEL_TOPICS_MAX) {
    return INSIGHT_WHEEL_TOPICS_MAX;
  }
  if (subTopics.length < INSIGHT_WHEEL_TOPICS_MIN) {
    return INSIGHT_WHEEL_TOPICS_MIN;
  }
  return subTopics.length;
};

export const getNumberOfSubTopics = (topics: CodingResult[]) =>
  topics.reduce(
    (acc: number, topic) => (topic.topics ? acc + topic.topics.length : acc),
    0,
  );

export const getFilteredSubtopics = (
  codingResults: CodingResult[],
  sentimentFilter: SentimentFilter,
): CodingResultsSubtopic[] | CodingResultSubTopicTotal[] => {
  let sentiment: SentimentCode | undefined = undefined;
  switch (sentimentFilter) {
    case 'negativeCount':
      sentiment = -1;
      break;
    case 'positiveCount':
      sentiment = 1;
      break;
    default:
      // totalOfAllCounts / default returns topicsTotal
      return getSubtopicsTotal(codingResults);
  }
  return codingResults.reduce((acc: CodingResultsSubtopic[], codingResult) => {
    const filteredSubTopics = codingResult.topics.filter(
      ({ sentimentCode }) => sentimentCode === sentiment,
    );
    if (filteredSubTopics.length) {
      return [...acc, ...filteredSubTopics];
    }
    return acc;
  }, []);
};

export const getRelevantTopicsWithSortedSubtopics = (
  topics: CodingResult[],
  codingResults: CodingResult[],
) =>
  topics.reduce((acc: CodingResult[], topic) => {
    const currentTopic = codingResults.find(
      ({ categoryLabel }) =>
        categoryLabel.toLowerCase() === topic.categoryLabel.toLowerCase(),
    );
    if (!currentTopic || !currentTopic.topics) return acc;
    currentTopic.topics.sort(
      ({ count: countA }, { count: countB }) => countB - countA,
    );
    return [...acc, currentTopic];
  }, []);
